<template>
  <CCard class="map-shell">
    <CCardHeader v-if="showHeader">
      <CIcon name="cil-map" />
      {{ title }}
      <div class="card-header-actions" v-if="subtitle">
        <small class="text-muted">{{ subtitle }}</small>
      </div>
    </CCardHeader>
    <CCardBody>
      <GmapMap
        v-if="canRenderGoogleMap"
        class="google-map-canvas"
        :center="resolvedCenter"
        :zoom="resolvedZoom"
        :style="mapCanvasStyle"
        @click="addMarker"
      >
        <GmapMarker
          v-for="(marker, index) in resolvedMarkers"
          :key="marker.id || index"
          :position="marker.position"
          :label="resolveMarkerLabel(marker)"
          :title="marker.title"
          :icon="resolveMarkerIcon(marker)"
          :clickable="true"
          :draggable="!!marker.draggable"
          @click="toggleInfoWindow(marker, index)"
        />
        <GmapInfoWindow
          :options="infoOptions"
          :position="infoWindowPos"
          :opened="infoWinOpen"
          @closeclick="infoWinOpen = false"
        >
          <div class="map-info-window">
            <div class="map-info-window__title">{{ infoContent }}</div>
            <div v-if="infoMeta" class="map-info-window__meta">{{ infoMeta }}</div>
            <a v-if="infoLink" :href="infoLink" target="_blank" rel="noreferrer noopener">Open link</a>
          </div>
        </GmapInfoWindow>
      </GmapMap>
      <div v-else-if="!hasGoogleMapsApiKey" class="map-empty-state map-empty-state--error">
        Google Maps is not configured. Set VUE_APP_GOOGLE_MAPS_API_KEY during the frontend build.
      </div>
      <div v-else class="map-empty-state map-empty-state--error">
        Google Maps rejected the configured API key. Check that the key is valid, billing is enabled, Maps JavaScript API is enabled, and the domain is allowed.
      </div>
      <div v-if="!resolvedMarkers.length && emptyMessage" class="map-empty-state">
        {{ emptyMessage }}
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import * as VueGoogleMaps from 'vue2-google-maps'
import Vue from 'vue'

if (typeof window !== 'undefined') {
  window.gm_authFailure = function () {
    window.__googleMapsAuthFailed = true
    window.dispatchEvent(new Event('google-maps-auth-error'))
  }
}

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY || ''
  }
})

const SAMPLE_MARKERS = [
  {
    id: 'stanford',
    position: { lat: 37.431489, lng: -122.163719 },
    label: 'S',
    draggable: false,
    title: 'Stanford',
    www: 'https://www.stanford.edu/'
  },
  {
    id: 'tesla',
    position: { lat: 37.394694, lng: -122.150333 },
    label: 'T',
    draggable: false,
    title: 'Tesla',
    www: 'https://www.tesla.com/'
  }
]

export default {
  name: 'google-maps',
  props: {
    markers: {
      type: Array,
      default: () => []
    },
    center: {
      type: Object,
      default: null
    },
    zoom: {
      type: Number,
      default: 2
    },
    mapHeight: {
      type: String,
      default: '400px'
    },
    title: {
      type: String,
      default: 'Google Map'
    },
    subtitle: {
      type: String,
      default: ''
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    useSampleMarkers: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      infoContent: '',
      infoMeta: '',
      infoLink: '',
      infoWindowPos: {
        lat: 0,
        lng: 0
      },
      infoWinOpen: false,
      currentMidx: null,
      mapAuthError: typeof window !== 'undefined' && !!window.__googleMapsAuthFailed,
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35
        }
      }
    }
  },
  computed: {
    hasGoogleMapsApiKey() {
      return !!String(process.env.VUE_APP_GOOGLE_MAPS_API_KEY || '').trim()
    },
    canRenderGoogleMap() {
      return this.hasGoogleMapsApiKey && !this.mapAuthError
    },
    mapCanvasStyle() {
      if (this.mapHeight === '100%') {
        return {
          height: '100%',
          minHeight: '280px',
          flex: '1 1 auto'
        }
      }
      return {
        height: this.mapHeight
      }
    },
    resolvedMarkers() {
      if (Array.isArray(this.markers) && this.markers.length) return this.markers
      return this.useSampleMarkers ? SAMPLE_MARKERS : []
    },
    resolvedCenter() {
      if (this.center && typeof this.center.lat === 'number' && typeof this.center.lng === 'number') {
        return this.center
      }
      const first = this.resolvedMarkers[0]
      if (first && first.position) return first.position
      return { lat: 13.7563, lng: 100.5018 }
    },
    resolvedZoom() {
      return this.resolvedMarkers.length > 1 ? this.zoom : Math.max(this.zoom - 1, 2)
    }
  },
  mounted() {
    window.addEventListener('google-maps-auth-error', this.handleGoogleMapsAuthError)
  },
  beforeDestroy() {
    window.removeEventListener('google-maps-auth-error', this.handleGoogleMapsAuthError)
  },
  methods: {
    handleGoogleMapsAuthError() {
      this.mapAuthError = true
    },
    resolveMarkerLabel(marker) {
      const value = marker && marker.label != null ? String(marker.label) : ''
      if (!value) return null
      return {
        text: value,
        color: '#ffffff',
        fontSize: '11px',
        fontWeight: '700'
      }
    },
    resolveMarkerIcon(marker) {
      if (marker && marker.icon) return marker.icon
      if (!(typeof window !== 'undefined' && window.google && window.google.maps)) return null
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
          <path fill="#d94841" stroke="#9f1d1d" stroke-width="1.5" d="M14 1.75c-6.18 0-11.19 5.01-11.19 11.19 0 8.55 11.19 21.31 11.19 21.31s11.19-12.76 11.19-21.31C25.19 6.76 20.18 1.75 14 1.75z"/>
          <circle cx="14" cy="13" r="4.4" fill="#fff5f5"/>
        </svg>
      `.trim()
      const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`

      return {
        url,
        scaledSize: new window.google.maps.Size(28, 36),
        anchor: new window.google.maps.Point(14, 36),
        labelOrigin: new window.google.maps.Point(14, 13)
      }
    },
    toggleInfoWindow(marker, idx) {
      this.infoWindowPos = marker.position
      this.infoContent = marker.title || 'Map location'
      this.infoMeta = marker.meta || ''
      this.infoLink = marker.www || marker.link || ''
      if (this.currentMidx === idx) {
        this.infoWinOpen = !this.infoWinOpen
      } else {
        this.currentMidx = idx
        this.infoWinOpen = true
      }
      this.$emit('select-marker', marker)
    },
    addMarker(event) {
      this.$emit('map-click', {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      })
    }
  }
}
</script>

<style scoped>
.map-shell {
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.map-shell :deep(.card-body) {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}

.google-map-canvas {
  width: 100%;
}

.map-info-window__title {
  color: #2d3748;
  font-weight: 700;
}

.map-info-window__meta {
  color: #718096;
  margin-top: 4px;
}

.map-empty-state {
  color: #718096;
  font-size: 0.92rem;
  padding-top: 12px;
}

.map-empty-state--error {
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #991b1b;
  font-weight: 700;
  padding: 18px;
}
</style>
